import React, { useEffect, useState } from 'react';
import { CheckIcon, GripVerticalIcon, PlusIcon } from 'lucide-react';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { createStage, setInitialStage, updateOrganization, updateStage } from '../../services/organizationService';
import { SECTOR_OPTIONS } from '../../data/sectors';
import { FONT_OPTIONS } from '../../data/fonts';
import { Organization } from '../../types';
import { PageHeader } from '../../components/crm/PageHeader';
import { BrandLogo } from '../../components/shared/BrandLogo';
import { LogoUploader } from '../../components/shared/LogoUploader';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Switch } from '../../components/ui/Switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
export function Settings() {
  const {
    organization
  } = useAuth();
  const {
    stages
  } = useWorkspace();
  const [draft, setDraft] = useState<Organization | null>(organization);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newStage, setNewStage] = useState('');
  useEffect(() => {
    if (organization && (!draft || draft.id !== organization.id)) setDraft(organization);
  }, [organization, draft]);
  if (!draft) return null;
  function update(partial: Partial<Organization>) {
    setDraft(prev => prev ? {
      ...prev,
      ...partial
    } : prev);
    setSaved(false);
  }
  async function handleSave() {
    setSaving(true);
    await updateOrganization(draft!.id, draft!);
    setSaving(false);
    setSaved(true);
  }
  return <>
      <PageHeader title="Configuración" description="Datos, identidad visual y preferencias de tu empresa." actions={<>
            {saved && <span className="flex items-center gap-1.5 self-center text-sm text-green-600" data-id="element-1239" data-mp="2uecmeyeaox60">
                <CheckIcon className="h-4 w-4" data-id="element-1240" data-mp="xdoxwb8z0tes" /> Guardado
              </span>}
            <Button onClick={handleSave} loading={saving} data-id="element-1241" data-mp="12c06yjrfyzvy">
              Guardar cambios
            </Button>
          </>} data-id="element-1238" data-mp="3m4qtaanw5986" />

      <Tabs defaultValue="empresa" data-id="element-1242" data-mp="176dw44figas6">
        <TabsList data-id="element-1243" data-mp="1c3e2kgg9vfjt">
          <TabsTrigger value="empresa" data-id="element-1244" data-mp="1c1t80qtyjwly">Empresa</TabsTrigger>
          <TabsTrigger value="identidad" data-id="element-1245" data-mp="efm29dlx3y80">Branding</TabsTrigger>
          <TabsTrigger value="pipeline" data-id="element-1246" data-mp="eh6x74c59j6e">Embudo de ventas</TabsTrigger>
          <TabsTrigger value="notificaciones" data-id="element-1247" data-mp="jfibbr9h2fuf">Notificaciones</TabsTrigger>
          <TabsTrigger value="privacidad" data-id="element-1248" data-mp="od0l1jvbcz3h">Privacidad</TabsTrigger>
        </TabsList>

        <TabsContent value="empresa" data-id="element-1249" data-mp="tcmtlg075r5y">
          <div className="grid gap-4 rounded-lg border border-border bg-background p-5 sm:grid-cols-2" data-id="element-1250" data-mp="tdobo4fmrdx6">
            <Input id="o-name" label="Nombre de la empresa" value={draft.name} onChange={e => update({
            name: e.target.value
          })} data-id="element-1251" data-mp="1zrxbmy75bwcy" />
            <Select id="o-sector" label="Sector" options={SECTOR_OPTIONS} value={draft.sector} onChange={e => update({
            sector: e.target.value
          })} data-id="element-1252" data-mp="3p8j512mulyxe" />
            <Input id="o-email" label="Email de contacto" type="email" value={draft.email ?? ''} onChange={e => update({
            email: e.target.value
          })} data-id="element-1253" data-mp="3fhy9xp6dvcj8" />
            <Input id="o-phone" label="Teléfono" value={draft.phone ?? ''} onChange={e => update({
            phone: e.target.value
          })} data-id="element-1254" data-mp="31jqghr8k2tct" />
            <Input id="o-web" label="Web" value={draft.website ?? ''} onChange={e => update({
            website: e.target.value
          })} data-id="element-1255" data-mp="2rmlqthfi4fr9" />
            <Input id="o-address" label="Dirección" value={draft.address ?? ''} onChange={e => update({
            address: e.target.value
          })} data-id="element-1256" data-mp="puyxvac1uas5" />
            <Select id="o-tz" label="Zona horaria" options={[{
            value: 'Europe/Madrid',
            label: 'Europe/Madrid'
          }, {
            value: 'Atlantic/Canary',
            label: 'Atlantic/Canary'
          }, {
            value: 'America/Mexico_City',
            label: 'America/Mexico_City'
          }, {
            value: 'America/Bogota',
            label: 'America/Bogota'
          }, {
            value: 'America/Argentina/Buenos_Aires',
            label: 'Buenos Aires'
          }]} value={draft.timezone} onChange={e => update({
            timezone: e.target.value
          })} data-id="element-1257" data-mp="3noshvj5kkz1g" />
            <Input id="o-logo" label="Logo (URL)" value={draft.logoUrl ?? ''} onChange={e => update({
            logoUrl: e.target.value
          })} data-id="element-1258" data-mp="2vwbcszko3njh" />
          </div>
        </TabsContent>

        <TabsContent value="identidad" data-id="element-1259" data-mp="2ly57e7znjz9v">
          <div className="space-y-6 rounded-2xl border border-border bg-background p-6 sm:p-8" data-id="element-1260" data-mp="22cvdwogqrynh">
            <p className="text-sm text-muted-foreground" data-id="element-1261" data-mp="1ybt97s422ue4">
              Este logotipo y estos colores se aplican automáticamente en el
              dashboard, el menú lateral, tus landings, tus formularios, el pie de
              página, la página de gracias y los emails.
            </p>

            <LogoUploader organizationId={draft.id} kind="logo" value={draft.brand.logoUrl ?? draft.logoUrl} onChange={value => update({
            logoUrl: value,
            brand: {
              ...draft.brand,
              logoUrl: value
            }
          })} data-id="element-1262" data-mp="2lbr3tnlkhqvi" />

            <Input id="b-name" label="Nombre de la empresa" value={draft.name} onChange={e => update({
            name: e.target.value
          })} data-id="element-1263" data-mp="2q394sbnlbrgh" />

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1264" data-mp="302gf0pz135zd">
              <Input id="b-primary" label="Color principal" type="color" value={draft.brand.primaryColor} onChange={e => update({
              brand: {
                ...draft.brand,
                primaryColor: e.target.value
              }
            })} data-id="element-1265" data-mp="3754w43oo4hsy" />
              <Input id="b-secondary" label="Color secundario" type="color" value={draft.brand.secondaryColor} onChange={e => update({
              brand: {
                ...draft.brand,
                secondaryColor: e.target.value
              }
            })} data-id="element-1266" data-mp="3lukiz8l2w33d" />
              <Input id="b-button" label="Color de botones" type="color" value={draft.brand.buttonColor ?? draft.brand.primaryColor} onChange={e => update({
              brand: {
                ...draft.brand,
                buttonColor: e.target.value
              }
            })} data-id="element-1267" data-mp="8mpvnncclt3o" />
              <Input id="b-text" label="Color de textos" type="color" value={draft.brand.textColor ?? '#0B0B0F'} onChange={e => update({
              brand: {
                ...draft.brand,
                textColor: e.target.value
              }
            })} data-id="element-1268" data-mp="22wek64ch7d6f" />
              <Input id="b-bg" label="Color de fondo" type="color" value={draft.brand.backgroundColor ?? '#FFFFFF'} onChange={e => update({
              brand: {
                ...draft.brand,
                backgroundColor: e.target.value
              }
            })} data-id="element-1269" data-mp="2lsw5o40qegu8" />
              <Input id="b-favicon" label="Favicon (URL)" value={draft.brand.faviconUrl ?? ''} onChange={e => update({
              brand: {
                ...draft.brand,
                faviconUrl: e.target.value
              }
            })} data-id="element-1270" data-mp="26y9j526xpeh1" />
              <Select id="b-font" label="Tipografía principal" options={FONT_OPTIONS} value={draft.brand.fontFamily} onChange={e => update({
              brand: {
                ...draft.brand,
                fontFamily: e.target.value
              }
            })} data-id="element-1271" data-mp="1l6yqx6vezd8d" />
              <Select id="b-font2" label="Tipografía secundaria" options={FONT_OPTIONS} value={draft.brand.secondaryFontFamily ?? draft.brand.fontFamily} onChange={e => update({
              brand: {
                ...draft.brand,
                secondaryFontFamily: e.target.value
              }
            })} data-id="element-1272" data-mp="23vn394fu0a8j" />
              <Input id="b-domain" label="Dominio propio" placeholder="paginas.miempresa.com" value={draft.brand.domain ?? ''} onChange={e => update({
              brand: {
                ...draft.brand,
                domain: e.target.value
              }
            })} data-id="element-1273" data-mp="2mtzecvqazu6r" />
            </div>

            <div className="rounded-xl border border-border bg-surface p-5" data-id="element-1274" data-mp="b51mv3zugxr8">
              <p className="mb-4 text-xs font-medium uppercase tracking-wider text-muted-foreground" data-id="element-1275" data-mp="pv0iaz00lv30">
                Vista previa
              </p>
              <div className="flex flex-wrap items-center gap-4 rounded-lg px-5 py-4" style={{
              backgroundColor: draft.brand.backgroundColor ?? '#FFFFFF',
              fontFamily: `${draft.brand.fontFamily}, Inter, sans-serif`
            }} data-id="element-1276" data-mp="3tbz69k6ku3mr">
                <BrandLogo src={draft.brand.logoUrl ?? draft.logoUrl} size={24} data-id="element-1277" data-mp="2567so364ilx" />
                <span className="text-sm font-semibold" style={{
                color: draft.brand.secondaryColor
              }} data-id="element-1278" data-mp="1ijxzszwejnup">
                  {draft.name}
                </span>
                <span className="text-sm" style={{
                color: draft.brand.textColor ?? '#5F6673',
                fontFamily: `${draft.brand.secondaryFontFamily ?? draft.brand.fontFamily}, Inter, sans-serif`
              }} data-id="element-1279" data-mp="jyht4hduksqd">
                  Texto de ejemplo
                </span>
                <span className="ml-auto inline-flex h-9 items-center rounded-lg px-4 text-sm font-medium text-white" style={{
                backgroundColor: draft.brand.buttonColor ?? draft.brand.primaryColor
              }} data-id="element-1280" data-mp="57hh62dw6gph">
                  Botón principal
                </span>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="pipeline" data-id="element-1281" data-mp="1lmis5htr6sug">
          <div className="rounded-lg border border-border bg-background p-5" data-id="element-1282" data-mp="1llhfxa1040b5">
            <ul className="space-y-2" data-id="element-1283" data-mp="1gp0g3ikfvxi8">
              {stages.map(stage => <li key={stage.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-border p-3" data-id="element-1284" data-mp="1o6yqdpag3cmi">
                  <GripVerticalIcon className="h-4 w-4 text-muted-foreground" aria-hidden="true" data-id="element-1285" data-mp="1xy2z479j218w" />
                  <Input id={`s-name-${stage.id}`} value={stage.name} onChange={e => updateStage(stage.id, {
                name: e.target.value
              })} data-id="element-1286" data-mp="22w4iyues24lf" />
                  <input type="color" aria-label={`Color de ${stage.name}`} value={stage.color} onChange={e => updateStage(stage.id, {
                color: e.target.value
              })} className="h-9 w-12 cursor-pointer rounded-md border border-border bg-background" data-id="element-1287" data-mp="1aa1xzjeohn3i" />
                  <Switch id={`s-active-${stage.id}`} label="Activo" checked={stage.active} onChange={checked => updateStage(stage.id, {
                active: checked
              })} data-id="element-1288" data-mp="2vk0evwwivsh5" />
                  <Button size="sm" variant={stage.isInitial ? 'primary' : 'ghost'} onClick={() => setInitialStage(stage.organizationId, stage.id)} data-id="element-1289" data-mp="14kij2gtukztj">
                    {stage.isInitial ? 'Estado inicial' : 'Marcar como inicial'}
                  </Button>
                </li>)}
            </ul>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row" data-id="element-1290" data-mp="ll4n5tck5f0e">
              <Input id="new-stage" placeholder="Nombre del nuevo estado" value={newStage} onChange={e => setNewStage(e.target.value)} data-id="element-1291" data-mp="lmfqxt606mri" />
              <Button leftIcon={<PlusIcon className="h-4 w-4" data-id="element-1293" data-mp="3l9fmpv24c6nb" />} disabled={!newStage.trim()} onClick={async () => {
              await createStage(draft.id, newStage.trim(), '#3D8BFF');
              setNewStage('');
            }} data-id="element-1292" data-mp="3p7v5tnv2wuvg">
                Añadir estado
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="notificaciones" data-id="element-1294" data-mp="365cu0tcq3bb8">
          <div className="space-y-4 rounded-lg border border-border bg-background p-5" data-id="element-1295" data-mp="366e3wui05rrd">
            {([['newLead', 'Nuevo lead'], ['leadAssigned', 'Lead asignado'], ['statusChange', 'Cambio de estado'], ['newMeeting', 'Nueva reunión']] as const).map(([key, label]) => <Switch key={key} id={`n-${key}`} label={label} checked={draft.notifications[key]} onChange={checked => update({
            notifications: {
              ...draft.notifications,
              [key]: checked
            }
          })} data-id="element-1296" data-mp="3ov2otgghjx35" />)}
          </div>
        </TabsContent>

        <TabsContent value="privacidad" data-id="element-1297" data-mp="2owldmuqeluqk">
          <div className="space-y-4 rounded-lg border border-border bg-background p-5" data-id="element-1298" data-mp="2ovipuvodo8ev">
            <Input id="p-privacy" label="URL de la política de privacidad" value={draft.privacy.privacyPolicyUrl} onChange={e => update({
            privacy: {
              ...draft.privacy,
              privacyPolicyUrl: e.target.value
            }
          })} data-id="element-1299" data-mp="2jw5xxwingb5f" />
            <Input id="p-legal" label="URL del aviso legal" value={draft.privacy.legalNoticeUrl} onChange={e => update({
            privacy: {
              ...draft.privacy,
              legalNoticeUrl: e.target.value
            }
          })} data-id="element-1300" data-mp="32nyvm48nykjh" />
            <Textarea id="p-consent" label="Texto de consentimiento" rows={3} value={draft.privacy.consentText} onChange={e => update({
            privacy: {
              ...draft.privacy,
              consentText: e.target.value
            }
          })} data-id="element-1301" data-mp="1m9qg2tmb9bhc" />
            <Textarea id="p-cookies" label="Texto de cookies" rows={3} value={draft.privacy.cookiesText} onChange={e => update({
            privacy: {
              ...draft.privacy,
              cookiesText: e.target.value
            }
          })} data-id="element-1302" data-mp="bpcb61p1uwhz" />
          </div>
        </TabsContent>
      </Tabs>
    </>;
}